import React, { useEffect, useState } from "react";
import { ResponsiveModal } from "@/components/ui/responsive-modal";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { client } from "@/lib/api";
import { Loader2, Mail, Lock, User as UserIcon, CheckCircle2 } from "lucide-react";

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z" />
  </svg>
);

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return (
      detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ") ||
      "Please check your details and try again."
    );
  if (detail && typeof detail.msg === "string") return detail.msg;
  return "Something went wrong. Please try again.";
}

const inputClass =
  "w-full rounded-xl border border-black/10 bg-white pl-10 pr-4 py-3 text-sm text-brand-navy outline-none focus:border-brand-teal transition-colors duration-200";

export function AuthDialog({ open, onOpenChange, initialMode = "signin" }) {
  const { login, loginWithPassword, registerWithPassword } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setError("");
      setBusy(false);
      setForgotSent(false);
    }
  }, [open, initialMode]);

  const isSignup = mode === "signup";
  const isForgot = mode === "forgot";

  const switchMode = (m) => {
    setMode(m);
    setError("");
    setForgotSent(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (isForgot) {
      if (!email) {
        setError("Please enter your email.");
        return;
      }
      setBusy(true);
      try {
        await client.post("/auth/forgot-password", { email: email.trim(), origin: window.location.origin });
        setForgotSent(true);
      } catch (err) {
        setForgotSent(true); // generic success — never reveal whether the account exists
      } finally {
        setBusy(false);
      }
      return;
    }

    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }
    if (isSignup && password.length < 8) {
      setError("Please choose a password of at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      if (isSignup) await registerWithPassword(email.trim(), password, name.trim());
      else await loginWithPassword(email.trim(), password);
      onOpenChange(false);
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const title = isForgot ? "Reset your password" : isSignup ? "Create your account" : "Welcome back";
  const description = isForgot
    ? "Enter your email and we'll send you a link to set a new password."
    : isSignup
    ? "Begin your journey — free, private, and judgment-free."
    : "Sign in to continue your journey.";

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange} testid="auth-dialog" title={title} description={description}>
      <div className="space-y-5 pb-2">
        {isForgot && forgotSent ? (
          <div className="text-center py-4" data-testid="forgot-sent">
            <div className="rounded-2xl bg-sage w-14 h-14 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-brand-teal-dark" />
            </div>
            <p className="text-brand-navy font-medium mb-1">Check your email</p>
            <p className="text-sm text-stone leading-relaxed">
              If an account exists for that email, we've sent a link to reset your password. It works for the next hour.
            </p>
            <button
              type="button"
              data-testid="forgot-back-btn"
              onClick={() => switchMode("signin")}
              className="mt-6 font-semibold text-brand-teal-dark hover:underline text-sm"
            >
              Back to sign in
            </button>
          </div>
        ) : (
          <>
            {!isForgot && (
              <>
                <button
                  type="button"
                  data-testid="auth-google-btn"
                  onClick={login}
                  className="w-full flex items-center justify-center gap-3 rounded-xl border border-black/10 px-5 py-3.5 font-medium text-brand-navy transition-colors duration-200 hover:bg-sage"
                >
                  <GoogleIcon /> Continue with Google
                </button>

                <div className="flex items-center gap-3 text-xs text-stone">
                  <div className="h-px flex-1 bg-border" />
                  <span>or with email</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
              </>
            )}

            <form onSubmit={submit} className="space-y-3">
              {isSignup && (
                <div className="relative">
                  <UserIcon className="w-4 h-4 text-stone absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    data-testid="auth-name-input"
                    type="text"
                    placeholder="First name (optional)"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={inputClass}
                  />
                </div>
              )}
              <div className="relative">
                <Mail className="w-4 h-4 text-stone absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="auth-email-input"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClass}
                />
              </div>
              {!isForgot && (
                <div className="relative">
                  <Lock className="w-4 h-4 text-stone absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    data-testid="auth-password-input"
                    type="password"
                    autoComplete={isSignup ? "new-password" : "current-password"}
                    placeholder={isSignup ? "Create a password (8+ characters)" : "Your password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={inputClass}
                  />
                </div>
              )}

              {!isForgot && !isSignup && (
                <div className="text-right">
                  <button
                    type="button"
                    data-testid="auth-forgot-link"
                    onClick={() => switchMode("forgot")}
                    className="text-sm text-brand-teal-dark hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              {error && (
                <p data-testid="auth-error" className="text-sm text-red-600 leading-relaxed">
                  {error}
                </p>
              )}

              <button
                data-testid="auth-submit-btn"
                type="submit"
                disabled={busy}
                className="w-full flex items-center justify-center gap-2 rounded-xl bg-brand-teal-dark px-5 py-3.5 text-white font-semibold transition-colors duration-200 hover:bg-[#0A6F66] disabled:opacity-60"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {isForgot ? "Send reset link" : isSignup ? "Create account" : "Sign in"}
              </button>
            </form>

            <p className="text-center text-sm text-stone">
              {isForgot ? (
                <>
                  Remembered it?{" "}
                  <button type="button" data-testid="auth-toggle-mode" onClick={() => switchMode("signin")} className="font-semibold text-brand-teal-dark hover:underline">
                    Back to sign in
                  </button>
                </>
              ) : isSignup ? (
                <>
                  Already have an account?{" "}
                  <button type="button" data-testid="auth-toggle-mode" onClick={() => switchMode("signin")} className="font-semibold text-brand-teal-dark hover:underline">
                    Sign in
                  </button>
                </>
              ) : (
                <>
                  New to Not By Will?{" "}
                  <button type="button" data-testid="auth-toggle-mode" onClick={() => switchMode("signup")} className="font-semibold text-brand-teal-dark hover:underline">
                    Create an account
                  </button>
                </>
              )}
            </p>
          </>
        )}
      </div>
    </ResponsiveModal>
  );
}
